// Core Service Initializer - централизованная инициализация системных сервисов
import { logger } from '@/engine/logging';
import { attachGlobalErrorHandlers } from '@/utils/errorOverlay';
import { domEventBridge } from '@/engine/events/DOMEventBridge';
import { eventBus } from '@/engine/events/EventBus';
import { InputManager } from '@/engine/input/InputManager';
import { PreGameLoader } from '@/engine/PreGameLoader';
import { settingsInitService } from '@/stores/game';
import { ServiceRegistry } from '@/engine/registry';
import { Game } from '@/engine/Game';
import { debugConsole } from '@/engine/debug';
import { panelPositioningService } from '@/engine/ui';

export class CoreServiceInitializer {
  private static isInitialized = false;

  /**
   * Инициализация базовых систем (логирование, ошибки, события, настройки)
   */
  public static async initializeBaseSystems(): Promise<void> {
    if (this.isInitialized) {
      logger.warn('⚠️ Core services already initialized', { source: 'CoreServiceInitializer' });
      return;
    }

    try {
      logger.info('🔧 Initializing core systems...', { source: 'CoreServiceInitializer' });

      // 1. Логирование и обработка ошибок
      attachGlobalErrorHandlers();

      // 2. PreGameLoader для загрузки шрифтов и базовых сервисов
      const preGameLoader = PreGameLoader.getInstance();
      await preGameLoader.load();

      // 3. Инициализация настроек
      logger.info('⚙️ Initializing settings...', { source: 'CoreServiceInitializer' });
      await settingsInitService.initialize();
      logger.info('✅ Settings initialized', { source: 'CoreServiceInitializer' });

      // 4. Инициализация DOM Event Bridge
      domEventBridge.initialize();

      this.isInitialized = true;
      logger.info('✅ Core systems initialized successfully', { source: 'CoreServiceInitializer' });
    } catch (error) {
      logger.error('❌ Failed to initialize core systems', error as Error, {
        source: 'CoreServiceInitializer',
      });
      throw error;
    }
  }

  /**
   * Регистрация игровых сервисов после инициализации Game
   */
  public static registerGameServices(gameInstance: Game): void {
    try {
      logger.info('🔧 Registering game services...', { source: 'CoreServiceInitializer' });

      // Регистрируем основные сервисы для debug console
      ServiceRegistry.register('Game', gameInstance);
      ServiceRegistry.register('SceneManager', gameInstance.getSceneManager());

      // Регистрируем игровые слои
      this.registerGameLayers(gameInstance);

      // Инициализируем debug console в dev режиме
      if (import.meta.env.DEV) {
        logger.info('🔧 Initializing debug console...', { source: 'CoreServiceInitializer' });
        debugConsole.initialize();
        logger.info('✅ Debug console ready', { source: 'CoreServiceInitializer' });
      }

      logger.info('✅ Game services registered', { source: 'CoreServiceInitializer' });
    } catch (error) {
      logger.error('❌ Failed to register game services', error as Error, {
        source: 'CoreServiceInitializer',
      });
      throw error;
    }
  }

  /**
   * Регистрация игровых слоев
   * Note: LayerManager создается в сценах, поэтому регистрация слоев происходит там
   */
  private static registerGameLayers(_gameInstance: Game): void {
    // LayerManager создается в сценах, поэтому здесь ничего не делаем
    // Игровые слои регистрируются при создании LayerManager в сценах
    logger.info('ℹ️ Game layers will be registered by scenes', {
      source: 'CoreServiceInitializer',
    });
  }

  /**
   * Очистка всех сервисов при teardown
   */
  public static cleanup(gameInstance?: Game | null): void {
    logger.info('🔥 Cleaning up core services...', { source: 'CoreServiceInitializer' });

    const safe = (label: string, action: () => void) => {
      try {
        action();
      } catch (error) {
        logger.warn(`⚠️ Cleanup step failed: ${label}`, {
          error: error instanceof Error ? error.message : String(error),
          source: 'CoreServiceInitializer',
        });
      }
    };

    // Очистка Game
    safe('gameInstance.destroy', () => {
      gameInstance?.destroy();
    });

    // Очистка DOM Event Bridge
    safe('DOMEventBridge.destroy', () => {
      domEventBridge.destroy();
    });

    // Очистка Input Manager
    safe('InputManager.resetInstance', () => {
      InputManager.resetInstance();
    });

    // Очистка Event Bus
    safe('eventBus.clear', () => {
      eventBus.clear();
    });

    // Очистка Service Registry
    safe('ServiceRegistry.clear', () => {
      ServiceRegistry.clear();
    });

    // Очистка PreGameLoader
    safe('PreGameLoader.resetInstance', () => {
      PreGameLoader.resetInstance();
    });

    // Очистка Panel Positioning Service
    safe('panelPositioningService.clearAll', () => {
      panelPositioningService.clearAll();
    });

    this.isInitialized = false;
    logger.info('✅ Core services cleanup complete', { source: 'CoreServiceInitializer' });
  }
}
